/**
 * The trainer's door on a phone (`D218`): a bubble tucked into the screen's
 * edge, over every screen — the workout included — because a question can
 * come up anywhere, and a tab only answers it from the bar.
 *
 * - A TAP opens the trainer over whatever is on screen (`overlay=coach`),
 *   exactly as the old "Personal" tab did; the bubble is not drawn while it
 *   is open.
 * - A DRAG moves it: it follows the finger and, let go, snaps to the nearer
 *   side, at the height it was dropped. A third of it stays past the edge,
 *   so it covers as little as a door can and still be a door. The spot is
 *   this device's (`localStorage`, read once): where a thumb rests is not
 *   something to sync.
 * - The click that ends a drag is swallowed, so moving it never opens it;
 *   a keyboard still activates it as a button.
 * - Above the screens, the tab bar and the full-screen overlays (z-45),
 *   under every sheet and dialog (z-50): a sheet's own buttons always win.
 * - The trainer's unread dot moved here from the tab it replaced.
 *
 * Desktop keeps the trainer as a sidebar row, where it docks beside the
 * screen; the bubble is phone-only.
 */
import {
  "shell.tab_coach" as coachLabel,
  "shell.tab_coach_unread" as coachUnread,
} from "@repo/i18n";
import { MessageCircle } from "lucide-react";
import {
  type CSSProperties,
  type MouseEvent,
  type PointerEvent,
  useCallback,
  useRef,
  useState,
} from "react";
import { cx } from "../../lib/cx";
import { Icon } from "../../ui/controls";
import { useNavBadges } from "./badges";
import { useNavigation } from "./navigation";

const SIZE_PX = 56;
/** How much of the bubble rests past the edge. */
const TUCK = 0.33;
/** A move shorter than this is still a tap. */
const DRAG_SLOP_PX = 8;
const STORAGE_KEY = "mypocketgym.coach-bubble.v1";
/** Where it starts: the right edge, a little under the middle. */
const DEFAULT_SPOT: Spot = { side: "right", y: 0.62 };

type Side = "left" | "right";

interface Spot {
  readonly side: Side;
  /** The bubble's centre, as a fraction of the viewport's height. */
  readonly y: number;
}

/** Through a function: Biome narrows a ref's `current` to its initial value. */
function flagOf(ref: { current: boolean }): boolean {
  return ref.current;
}

function readSpot(): Spot {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const value: unknown = raw === null ? null : JSON.parse(raw);
    if (
      typeof value === "object" &&
      value !== null &&
      "side" in value &&
      "y" in value &&
      (value.side === "left" || value.side === "right") &&
      typeof value.y === "number" &&
      value.y >= 0 &&
      value.y <= 1
    ) {
      return { side: value.side, y: value.y };
    }
  } catch {
    // Storage unavailable or a bad value: the default spot is fine.
  }
  return DEFAULT_SPOT;
}

function writeSpot(spot: Spot): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(spot));
  } catch {
    // Not persisted; it stays where it was dropped until the app reloads.
  }
}

/**
 * The rest position. Clamped clear of the status bar and the header's
 * buttons at the top, and of the tab bar and the central button at the foot.
 */
function restStyle(spot: Spot): CSSProperties {
  const tuck = `${-SIZE_PX * TUCK}px`;
  return {
    top: `clamp(calc(env(safe-area-inset-top) + 88px), calc(${spot.y * 100}dvh - ${SIZE_PX / 2}px), calc(100dvh - env(safe-area-inset-bottom) - 168px))`,
    ...(spot.side === "right" ? { right: tuck } : { left: tuck }),
  };
}

export function CoachBubble() {
  const { go } = useNavigation();
  const badges = useNavBadges();
  const [spot, setSpot] = useState<Spot>(readSpot);
  const [dragAt, setDragAt] = useState<{ x: number; y: number } | null>(null);
  const press = useRef<{ id: number; x: number; y: number } | null>(null);
  const dragged = useRef(false);

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      press.current = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      };
      dragged.current = false;
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    []
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      const start = press.current;
      if (start === null || start.id !== event.pointerId) {
        return;
      }
      const distance = Math.hypot(
        event.clientX - start.x,
        event.clientY - start.y
      );
      if (!flagOf(dragged) && distance < DRAG_SLOP_PX) {
        return;
      }
      dragged.current = true;
      setDragAt({ x: event.clientX, y: event.clientY });
    },
    []
  );

  const onPointerUp = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    const start = press.current;
    press.current = null;
    if (start === null || start.id !== event.pointerId || !flagOf(dragged)) {
      return;
    }
    const next: Spot = {
      side: event.clientX < window.innerWidth / 2 ? "left" : "right",
      y: Math.min(1, Math.max(0, event.clientY / window.innerHeight)),
    };
    setSpot(next);
    writeSpot(next);
    setDragAt(null);
  }, []);

  const onPointerCancel = useCallback(() => {
    press.current = null;
    dragged.current = false;
    setDragAt(null);
  }, []);

  const onClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (flagOf(dragged)) {
        dragged.current = false;
        event.preventDefault();
        return;
      }
      go("coach");
    },
    [go]
  );

  const unread = badges.trainerUnread;
  const style: CSSProperties =
    dragAt === null
      ? restStyle(spot)
      : { left: dragAt.x - SIZE_PX / 2, top: dragAt.y - SIZE_PX / 2 };

  return (
    <button
      aria-label={unread ? coachUnread() : coachLabel()}
      className={cx(
        "fixed z-45 grid size-14 touch-none select-none place-items-center rounded-full bg-ink text-canvas shadow-lg lg:hidden",
        dragAt === null &&
          "transition-[left,right,top] duration-200 ease-out motion-reduce:transition-none",
        dragAt === null && (spot.side === "right" ? "pr-4" : "pl-4")
      )}
      data-coach-bubble={spot.side}
      onClick={onClick}
      onPointerCancel={onPointerCancel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={style}
      title={coachLabel()}
      type="button"
    >
      <Icon icon={MessageCircle} size={24} />
      {unread ? (
        <span
          aria-hidden="true"
          className={cx(
            "absolute top-1 size-3 rounded-full bg-accent ring-2 ring-ink",
            spot.side === "right" ? "left-1.5" : "right-1.5"
          )}
          data-nav-badge
        />
      ) : null}
    </button>
  );
}
